import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';

// Multi-value chip input with optional autocomplete suggestions.
// value: string[]  onChange: (string[]) => void  options?: string[] (freeSolo allowed unless restrictToOptions)
// restrictToOptions: when true, only a value matching (case-insensitively) an entry in
// `options` can be added — used where the tag must resolve to a real, known record
// rather than accepting arbitrary text.
export default function TagInput({ label, value = [], onChange, options = [], placeholder = '', restrictToOptions = false }) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const suggestions = useMemo(() => {
    if (!input.trim()) return [];
    const q = input.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q) && !value.includes(o)).slice(0, 8);
  }, [input, options, value]);

  const addTag = (tag) => {
    let trimmed = tag.trim();
    if (!trimmed) return;
    if (restrictToOptions) {
      const match = options.find((o) => o.toLowerCase() === trimmed.toLowerCase());
      if (!match) return;
      trimmed = match; // use the option's canonical casing, not whatever the user typed
    }
    if (value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInput('');
    setShowSuggestions(false);
  };

  const removeTag = (tag) => onChange(value.filter((v) => v !== tag));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && value.length) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div className="relative">
      {label && <label className="form-label">{label}</label>}
      <div className="form-input flex flex-wrap gap-1.5 items-center min-h-[44px]">
        {value.map((tag) => (
          <span key={tag} className="badge badge-blue">
            {tag}
            <button type="button" onClick={() => removeTag(tag)} className="ml-0.5">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[100px] outline-none text-sm border-none py-0.5"
          value={input}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(e) => {
            setInput(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-56 overflow-y-auto dark:bg-ink-900 dark:border-neutral-700">
          {suggestions.map((s) => (
            <button
              type="button"
              key={s}
              className="w-full text-left px-3.5 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-ink-800"
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
